import React from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { cn } from "@gettactic/helpers";

import {
  Category,
  ContentBlock,
  Mention,
  Suggestion,
  TriggerConfig
} from "@v2/components/form/MentionTextarea/types";
import {
  contentBlocksToText,
  textToContentBlocks
} from "@v2/components/form/MentionTextarea/helpers";

export interface MentionTextareaProps {
  value?: ContentBlock[];
  initialValue?: ContentBlock[];
  onChange?: (value: ContentBlock[]) => void;
  suggestions: Category[];
  triggerChars: TriggerConfig;
  onSubmit?: (value: ContentBlock[]) => void;
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  suggestions: customSuggestions,
  triggerChars,
  initialValue,
  onSubmit
}) => {
  const [text, setText] = React.useState("");
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<Category[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [triggerPosition, setTriggerPosition] = React.useState<{
    start: number;
    end: number;
  } | null>(null);
  const [mentions, setMentions] = React.useState<Mention[]>([]);
  const [isEditingMention, setIsEditingMention] = React.useState(false);

  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const isInternalUpdateRef = React.useRef(false);

  const [popperElement, setPopperElement] =
    React.useState<HTMLDivElement | null>(null);
  const caretRectRef = React.useRef<DOMRect>(new DOMRect(0, 0, 0, 0));

  const textAreaRef = React.useRef<HTMLDivElement>(null);

  const { styles, attributes, update } = usePopper(
    textAreaRef.current,
    popperElement,
    {
      placement: "bottom-start",
      modifiers: [
        { name: "offset", options: { offset: [0, 8] } },
        { name: "flip", options: { fallbackPlacements: ["top-start"] } },
        {
          name: "preventOverflow",
          // @ts-ignore
          options: { padding: 8, boundary: "viewport" }
        }
      ]
    }
  );

  const allSuggestions: Category[] = customSuggestions;

  const handleOnSubmit = () => {
    setText("");
    setMentions([]);
    setTriggerPosition(null);
    setShowSuggestions(false);
    setSelectedIndex(0);
    if (onSubmit) {
      const blocks = textToContentBlocks(text, mentions);
      onSubmit(blocks);
    }
  };

  const getTotalSuggestions = (categories: Category[]) =>
    categories.reduce((acc, cat) => acc + cat.suggestions.length, 0);

  const getSuggestionByIndex = (categories: Category[], index: number) => {
    let currentIndex = 0;
    for (const category of categories) {
      if (index < currentIndex + category.suggestions.length) {
        return category.suggestions[index - currentIndex];
      }
      currentIndex += category.suggestions.length;
    }
    return null;
  };

  const findMentionsInText = (value: string): Mention[] => {
    const foundMentions: Mention[] = [];
    const flatSuggestions = allSuggestions.flatMap((cat) => cat.suggestions);

    flatSuggestions.forEach((suggestion) => {
      let index = 0;
      while ((index = value.indexOf(suggestion.value, index)) !== -1) {
        const beforeChar = index > 0 ? value[index - 1] : " ";
        const afterChar =
          index + suggestion.value.length < value.length
            ? value[index + suggestion.value.length]
            : " ";

        if (
          (beforeChar === " " || beforeChar === "\n" || index === 0) &&
          (afterChar === " " ||
            afterChar === "\n" ||
            index + suggestion.value.length === value.length)
        ) {
          // Check if this mention overlaps with any existing mention
          // This don't prevent us from having multiple mentions with the same value
          // but it prevents bug with accidentally double insertions
          const overlaps = foundMentions.some(
            (existing) =>
              (index >= existing.start && index < existing.end) ||
              (index + suggestion.value.length > existing.start &&
                index + suggestion.value.length <= existing.end) ||
              (index <= existing.start &&
                index + suggestion.value.length >= existing.end)
          );

          if (!overlaps) {
            foundMentions.push({
              start: index,
              end: index + suggestion.value.length,
              value: suggestion.value,
              trigger: suggestion.value[0],
              id: suggestion.id,
              label: suggestion.label
            });
          }
        }
        index += suggestion.value.length;
      }
    });

    return foundMentions.sort((a, b) => a.start - b.start);
  };

  React.useEffect(() => {
    const foundMentions = findMentionsInText(text);
    setMentions(foundMentions);

    if (onChange) {
      isInternalUpdateRef.current = true;
      const blocks = textToContentBlocks(text, foundMentions);
      onChange(blocks);
    }
  }, [text, onChange]);

  const detectTrigger = (value: string, cursorPos: number) => {
    const beforeCursor = value.substring(0, cursorPos);

    for (const [trigger, categoryName] of Object.entries(triggerChars)) {
      const lastTriggerIndex = beforeCursor.lastIndexOf(trigger);

      if (lastTriggerIndex !== -1) {
        const afterTrigger = beforeCursor.substring(lastTriggerIndex + 1);
        const hasWhitespace = /\s/.test(afterTrigger);

        if (!hasWhitespace) {
          const searchTerm = afterTrigger.toLowerCase();
          const filteredCategories = allSuggestions
            .map((cat) => ({
              ...cat,
              suggestions: cat.suggestions.filter(
                (s) =>
                  s.category === categoryName &&
                  s.label.toLowerCase().includes(searchTerm)
              )
            }))
            .filter((cat) => cat.suggestions.length > 0);

          if (filteredCategories.length > 0) {
            return {
              trigger,
              start: lastTriggerIndex,
              end: cursorPos,
              categories: filteredCategories
            };
          }
        }
      }
    }

    return null;
  };

  const updateCaretRect = React.useCallback(() => {
    if (!textareaRef.current || triggerPosition === null) return;

    const ta = textareaRef.current;
    const taStyle = window.getComputedStyle(ta);
    const taRect = ta.getBoundingClientRect();

    const mirror = document.createElement("div");
    mirror.style.position = "absolute";
    mirror.style.visibility = "hidden";
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.wordWrap = "break-word";
    mirror.style.overflowWrap = "anywhere";
    mirror.style.boxSizing = "border-box";
    mirror.style.left = `${taRect.left + window.scrollX - ta.scrollLeft}px`;
    mirror.style.top = `${taRect.top + window.scrollY - ta.scrollTop}px`;
    mirror.style.width = `${ta.clientWidth}px`;
    mirror.style.paddingLeft = taStyle.paddingLeft;
    mirror.style.paddingRight = taStyle.paddingRight;
    mirror.style.paddingTop = taStyle.paddingTop;
    mirror.style.paddingBottom = taStyle.paddingBottom;

    const copy = [
      "fontFamily",
      "fontSize",
      "fontWeight",
      "fontStyle",
      "letterSpacing",
      "textTransform",
      "textIndent",
      "textAlign",
      "lineHeight",
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
      "borderTopWidth",
      "borderRightWidth",
      "borderBottomWidth",
      "borderLeftWidth",
      "boxSizing"
    ] as const;
    copy.forEach((k) => (mirror.style[k] = taStyle[k] as string));

    const before = text
      .substring(0, triggerPosition.end)
      .replace(/ /g, "\u00a0")
      .replace(/\n/g, "\n");

    const textNode = document.createTextNode(before);
    const mark = document.createElement("span");
    mark.textContent = "\u200b"; // zero-width marker

    mirror.appendChild(textNode);
    mirror.appendChild(mark);

    document.body.appendChild(mirror);

    const markRect = mark.getBoundingClientRect();
    const lineH = Number.parseFloat(taStyle.lineHeight || "20") || 20;

    const top = markRect.top - taRect.top;
    const left = markRect.left - taRect.left;

    const rect = new DOMRect(left, top, 1, lineH);
    caretRectRef.current = rect;

    document.body.removeChild(mirror);

    update?.();
  }, [text, triggerPosition, update]);

  React.useLayoutEffect(() => {
    if (showSuggestions) {
      updateCaretRect();
    }
  }, [showSuggestions, triggerPosition, text, updateCaretRect]);

  React.useEffect(() => {
    if (!showSuggestions) return;
    const onScrollOrResize = () => updateCaretRect();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    const ta = textareaRef.current;
    ta?.addEventListener("scroll", onScrollOrResize);

    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      ta?.removeEventListener("scroll", onScrollOrResize);
    };
  }, [showSuggestions, updateCaretRect]);

  // Sync scroll between textarea and overlay
  React.useEffect(() => {
    const ta = textareaRef.current;
    const overlay = overlayRef.current;

    if (!ta || !overlay) return;

    const handleScroll = () => {
      overlay.scrollTop = ta.scrollTop;
      overlay.scrollLeft = ta.scrollLeft;
    };

    ta.addEventListener("scroll", handleScroll);
    return () => ta.removeEventListener("scroll", handleScroll);
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;

    setText(value);

    const result = detectTrigger(value, cursorPos);

    if (result) {
      setTriggerPosition({ start: result.start, end: result.end });
      setSuggestions(result.categories);
      setShowSuggestions(true);
      setSelectedIndex(0);
    } else {
      setShowSuggestions(false);
      setTriggerPosition(null);
      setIsEditingMention(false);
    }
  };

  React.useEffect(() => {
    if (initialValue && initialValue.length > 0) {
      const { text: newText, mentions: newMentions } =
        contentBlocksToText(initialValue);
      setText(newText);
      setMentions(newMentions);
      isInternalUpdateRef.current = false;
    }
  }, [initialValue]);

  const insertSuggestion = (suggestion: Suggestion) => {
    if (!triggerPosition || !textareaRef.current) return;

    const ta = textareaRef.current;
    const scrollTop = ta.scrollTop;
    const scrollLeft = ta.scrollLeft;

    const before = text.substring(0, triggerPosition.start);
    const after = text.substring(triggerPosition.end);

    const spaceAfter = after.startsWith(" ") ? "" : " ";
    const newText = before + suggestion.value + spaceAfter + after;

    const newMention: Mention = {
      start: triggerPosition.start,
      end: triggerPosition.start + suggestion.value.length,
      value: suggestion.value,
      trigger: suggestion.value[0],
      id: suggestion.id,
      label: suggestion.label
    };

    const updatedMentions = mentions
      .filter(
        (m) =>
          !(m.start === triggerPosition.start && m.end === triggerPosition.end)
      )
      .concat(newMention)
      .sort((a, b) => a.start - b.start);

    setText(newText);
    setMentions(updatedMentions);
    setShowSuggestions(false);
    setTriggerPosition(null);
    setIsEditingMention(false);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const newCursorPos =
          before.length + suggestion.value.length + spaceAfter.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        // Restore scroll position
        textareaRef.current.scrollTop = scrollTop;
        textareaRef.current.scrollLeft = scrollLeft;
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions) {
      const totalSuggestions = getTotalSuggestions(suggestions);

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalSuggestions);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(
          (prev) => (prev - 1 + totalSuggestions) % totalSuggestions
        );
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const suggestion = getSuggestionByIndex(suggestions, selectedIndex);
        if (suggestion) insertSuggestion(suggestion);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        setTriggerPosition(null);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleOnSubmit();
    }
  };

  const handleMentionClick = (
    e: React.MouseEvent<HTMLSpanElement>,
    mention: Mention
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (!textareaRef.current) return;

    const categoryName = triggerChars[mention.trigger];
    const filteredCategories = customSuggestions
      .map((cat) => ({
        ...cat,
        suggestions: cat.suggestions.filter((s) => s.category === categoryName)
      }))
      .filter((cat) => cat.suggestions.length > 0);

    setIsEditingMention(true);
    setTriggerPosition({ start: mention.start, end: mention.end });
    setSuggestions(filteredCategories);
    setShowSuggestions(true);
    setSelectedIndex(0);

    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(mention.end, mention.end);
  };

  React.useEffect(() => {
    if (showSuggestions && popperElement) {
      const selectedElement = popperElement.querySelector(
        `[data-index="${selectedIndex}"]`
      );
      selectedElement?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedIndex, showSuggestions, popperElement]);

  const renderHighlightedText = () => {
    if (mentions.length === 0 && !triggerPosition) {
      return <span className="pointer-events-none">{text}</span>;
    }

    const elements: JSX.Element[] = [];
    let lastIndex = 0;

    let allHighlights = [...mentions];
    if (triggerPosition) {
      allHighlights = allHighlights.filter(
        (m) =>
          !(m.start === triggerPosition.start && m.end === triggerPosition.end)
      );
      allHighlights.push({
        start: triggerPosition.start,
        end: triggerPosition.end,
        value: text.substring(triggerPosition.start, triggerPosition.end),
        trigger: text[triggerPosition.start]
      } as Mention);
    }

    allHighlights.sort((a, b) => a.start - b.start);

    allHighlights.forEach((highlight, index) => {
      if (highlight.start > lastIndex) {
        elements.push(
          <span key={`text-${index}`} className="pointer-events-none">
            {text.substring(lastIndex, highlight.start)}
          </span>
        );
      }

      const isActiveTrigger =
        triggerPosition &&
        highlight.start === triggerPosition.start &&
        highlight.end === triggerPosition.end;

      elements.push(
        <span
          key={`mention-${index}`}
          className="pointer-events-auto relative inline-block"
          onClick={(e) => !isActiveTrigger && handleMentionClick(e, highlight)}
        >
          <span
            className={`${
              isActiveTrigger
                ? "text-cyan-900 hover:text-blue-900 "
                : "cursor-pointer text-blue-600 hover:text-cyan-600 transition-all"
            } rounded shadow-sm`}
          >
            {text.substring(highlight.start, highlight.end)}
          </span>
        </span>
      );

      lastIndex = highlight.end;
    });

    if (lastIndex < text.length) {
      elements.push(
        <span key="text-end" className="pointer-events-none">
          {text.substring(lastIndex)}
        </span>
      );
    }

    return elements;
  };

  return (
    <div className="mx-auto w-full" ref={textAreaRef}>
      <div className="relative">
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything! Try typing @ # to see suggestions..."
            className={cn(
              "relative w-full resize-none rounded-lg border-2 border-gray-300 p-3 font-mono text-base text-transparent leading-6",
              "w-full resize-none rounded-none border-none shadow-none outline-none ring-0",
              "max-h-[7.5rem] min-h-[2.5rem] overflow-y-auto bg-transparent dark:bg-transparent",
              "focus-visible:ring-0"
            )}
            style={{
              caretColor: "black",
              lineHeight: "1.5rem",
              letterSpacing: "0px",
              wordWrap: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap"
            }}
          />

          <div
            ref={overlayRef}
            className="pointer-events-none absolute inset-0 overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-base text-black leading-6"
            style={{
              lineHeight: "1.5rem",
              letterSpacing: "0px",
              wordWrap: "break-word",
              overflowWrap: "anywhere",
              whiteSpace: "pre-wrap"
            }}
          >
            {renderHighlightedText()}
          </div>
        </div>

        {showSuggestions &&
          createPortal(
            <div
              ref={setPopperElement}
              style={styles.popper as React.CSSProperties}
              {...attributes.popper}
              className="z-[9999999] max-h-80 w-72 overflow-y-auto rounded-lg border-2 border-gray-300 bg-white shadow-xl"
              aria-orientation="vertical"
            >
              {suggestions.map((category, catIndex) => {
                const Icon = category.icon;
                const startIndex = suggestions
                  .slice(0, catIndex)
                  .reduce((acc, cat) => acc + cat.suggestions.length, 0);

                return (
                  <div key={category.name} className="py-1">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2 sticky top-0 bg-white">
                      <Icon className="w-3 h-3" />
                      {category.name}
                    </div>
                    {category.suggestions.map((suggestion, index) => {
                      const globalIndex = startIndex + index;
                      const active = selectedIndex === globalIndex;
                      return (
                        <button
                          key={suggestion.id}
                          data-index={globalIndex}
                          onClick={() => insertSuggestion(suggestion)}
                          className={cn(
                            "w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors",
                            active && "bg-blue-100"
                          )}
                          aria-selected={active}
                        >
                          <div className="font-medium text-gray-900">
                            {suggestion.label}
                          </div>
                          <div className="text-sm text-gray-500">
                            {suggestion.value}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>,
            window.document.body
          )}
      </div>
    </div>
  );
};
